import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Pagination,
  Spin,
  message,
} from 'antd';
import { getNasFiles } from '@/nasRequest';
import request from '@/request';
import logo from '@/assets/logo.svg';
import ItemTabs from './ItemTabs';

const VIDEO_EXTENSION_PATTERN = /\.(mp4|webm|mov)$/i;
const EMPTY_CATEGORY_PREFIX = 'empty-item:';
const EMPTY_VIDEO_FOLDER_MESSAGE = '当前赛事视频文件夹为空，请先上传视频';

const createEmptyCategory = (item) => ({
  id: `${EMPTY_CATEGORY_PREFIX}${item.id}`,
  name: item.name,
  groupName: item.groupName || '',
  path: `${EMPTY_CATEGORY_PREFIX}${item.id}`,
  hasVideoDirectory: false,
});

const formatFileSize = (size = 0) => {
  if (!size) return '0 B';
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const isNasConnectionError = (error) =>
  error instanceof TypeError || error?.message === 'Failed to fetch';

const getErrorMessage = (error) => {
  if (error?.status === 404 && error?.code === 'path not found') {
    return EMPTY_VIDEO_FOLDER_MESSAGE;
  }
  if (isNasConnectionError(error)) {
    return '视频服务连接失败';
  }
  return error?.message || '视频服务连接失败';
};

const downloadFile = async (url, filename) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`视频下载失败：${response.status}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;

  try {
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
};

const VideoThumbnail = ({ video }) => {
  const [ready, setReady] = useState(false);

  return (
    <>
      <img
        src={logo}
        alt=""
        className={`pointer-events-none absolute h-16 w-16 object-contain transition-opacity ${
          ready ? 'opacity-0' : 'opacity-100'
        }`}
      />
      <video
        aria-hidden="true"
        muted
        playsInline
        preload="metadata"
        src={video.url}
        className={`pointer-events-none absolute inset-0 h-full w-full object-cover transition-opacity ${
          ready ? 'opacity-100' : 'opacity-0'
        }`}
        onLoadedMetadata={(event) => {
          const duration = event.currentTarget.duration;
          if (Number.isFinite(duration) && duration > 0) {
            event.currentTarget.currentTime = Math.min(0.001, duration / 2);
          }
        }}
        onLoadedData={() => setReady(true)}
        onError={() => setReady(false)}
      />
    </>
  );
};

export default function VideoManagement({ event }) {
  const [categories, setCategories] = useState([]);
  const [activePath, setActivePath] = useState('');
  const [videos, setVideos] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [nasIpAddr, setNasIpAddr] = useState('');
  const [videoError, setVideoError] = useState('');
  const [activeVideo, setActiveVideo] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCategories = async () => {
      if (!event?.name) {
        setCategories([]);
        setActivePath('');
        setNasIpAddr('');
        setCategoryError('赛事名称为空，无法读取视频目录');
        return;
      }

      setCategoryLoading(true);
      setCategoryError('');
      setNasIpAddr('');
      setCategories([]);
      setActivePath('');
      setVideos([]);
      setKeyword('');
      setSelectedPaths([]);
      setCurrentPage(1);

      try {
        const nasInfo = await request.get('/events/admin/nas/info');
        const nextNasIpAddr = nasInfo?.nasIpAddr?.trim();

        if (!nextNasIpAddr) {
          throw new Error('未获取到 NAS 地址');
        }

        if (!cancelled) {
          setNasIpAddr(nextNasIpAddr);
        }

        const rootResult = await getNasFiles(event.name, nextNasIpAddr);

        if (!rootResult.items.length) {
          const emptyCategories = (event.items || []).map(createEmptyCategory);

          if (!cancelled) {
            if (emptyCategories.length) {
              setCategories(emptyCategories);
              setActivePath(emptyCategories[0].path);
            } else {
              setCategoryError(EMPTY_VIDEO_FOLDER_MESSAGE);
            }
          }
          return;
        }

        const rootDirectories = rootResult.items.filter(
          (item) => item.type === 'dir'
        );
        const rootFiles = rootResult.items.filter(
          (item) => item.type === 'file' && VIDEO_EXTENSION_PATTERN.test(item.name)
        );
        const nextCategories = [];

        if (rootFiles.length) {
          nextCategories.push({
            id: rootResult.path,
            name: '全部视频',
            groupName: '',
            path: rootResult.path,
            hasVideoDirectory: true,
          });
        }

        const childrenResults = await Promise.all(
          rootDirectories.map(async (directory) => ({
            directory,
            result: await getNasFiles(directory.path, nextNasIpAddr),
          }))
        );

        childrenResults.forEach(({ directory, result }) => {
          const groupDirectories = result.items.filter(
            (item) => item.type === 'dir'
          );
          const directVideos = result.items.some(
            (item) =>
              item.type === 'file' && VIDEO_EXTENSION_PATTERN.test(item.name)
          );

          if (directVideos || !groupDirectories.length) {
            nextCategories.push({
              id: directory.path,
              name: directory.name,
              groupName: '',
              path: directory.path,
              hasVideoDirectory: true,
            });
          }

          groupDirectories.forEach((group) => {
            nextCategories.push({
              id: group.path,
              name: directory.name,
              groupName: group.name,
              path: group.path,
              hasVideoDirectory: true,
            });
          });
        });

        (event.items || []).forEach((item) => {
          const categoryExists = nextCategories.some(
            (category) =>
              category.name === item.name &&
              category.groupName === (item.groupName || '')
          );

          if (!categoryExists) {
            nextCategories.push(createEmptyCategory(item));
          }
        });

        if (!cancelled) {
          setCategories(nextCategories);
          setActivePath(nextCategories[0]?.path || '');
        }
      } catch (error) {
        if (!cancelled) {
          const isEmptyVideoFolder =
            error?.status === 404 && error?.code === 'path not found';
          const emptyCategories = (event.items || []).map(createEmptyCategory);

          if (isEmptyVideoFolder && emptyCategories.length) {
            setCategories(emptyCategories);
            setActivePath(emptyCategories[0].path);
          } else {
            setCategoryError(getErrorMessage(error));
          }
        }
      } finally {
        if (!cancelled) {
          setCategoryLoading(false);
        }
      }
    };

    loadCategories();

    return () => {
      cancelled = true;
    };
  }, [event?.items, event?.name]);

  useEffect(() => {
    let cancelled = false;

    const loadVideos = async () => {
      const activeCategory = categories.find(
        (category) => category.path === activePath
      );

      if (
        !activePath ||
        !nasIpAddr ||
        activeCategory?.hasVideoDirectory === false
      ) {
        setVideoLoading(false);
        setVideoError(activeCategory ? '当前赛项暂无视频' : '');
        setVideos([]);
        setKeyword('');
        setSelectedPaths([]);
        setCurrentPage(1);
        return;
      }

      setVideoLoading(true);
      setVideoError('');
      setVideos([]);
      setSelectedPaths([]);
      setCurrentPage(1);

      try {
        const result = await getNasFiles(activePath, nasIpAddr);
        const nextVideos = result.items.filter(
          (item) => item.type === 'file' && VIDEO_EXTENSION_PATTERN.test(item.name)
        );

        if (!cancelled) {
          setVideos(nextVideos);
        }
      } catch (error) {
        if (!cancelled) {
          setVideoError(getErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setVideoLoading(false);
        }
      }
    };

    loadVideos();

    return () => {
      cancelled = true;
    };
  }, [activePath, categories, nasIpAddr]);

  const filteredVideos = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    if (!normalizedKeyword) return videos;
    return videos.filter((video) =>
      video.name.toLowerCase().includes(normalizedKeyword)
    );
  }, [keyword, videos]);

  const pagedVideos = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVideos.slice(start, start + pageSize);
  }, [currentPage, filteredVideos, pageSize]);

  const activeCategory = useMemo(
    () => categories.find((category) => category.path === activePath),
    [activePath, categories]
  );

  const allFilteredSelected =
    filteredVideos.length > 0 &&
    filteredVideos.every((video) => selectedPaths.includes(video.path));
  const partiallySelected =
    !allFilteredSelected &&
    filteredVideos.some((video) => selectedPaths.includes(video.path));

  const handleKeywordChange = (value) => {
    setKeyword(value);
    setCurrentPage(1);
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPaths(filteredVideos.map((video) => video.path));
      return;
    }
    setSelectedPaths([]);
  };

  const handleSelectVideo = (path, checked) => {
    setSelectedPaths((previous) =>
      checked
        ? [...previous, path]
        : previous.filter((selectedPath) => selectedPath !== path)
    );
  };

  const handleDownload = async () => {
    const selectedVideos = videos.filter((video) =>
      selectedPaths.includes(video.path)
    );

    if (!selectedVideos.length) {
      message.warning('请先选择需要下载的视频');
      return;
    }

    const messageKey = 'video-download';
    const failedVideos = [];
    setDownloadLoading(true);

    try {
      for (const [index, video] of selectedVideos.entries()) {
        message.loading({
          content: `正在下载 ${index + 1}/${selectedVideos.length}：${video.name}`,
          duration: 0,
          key: messageKey,
        });

        try {
          await downloadFile(video.url, video.name);
        } catch (error) {
          failedVideos.push(video);
          console.error('Video download error:', error);
        }
      }

      if (!failedVideos.length) {
        message.success({ content: '下载完成', key: messageKey });
      } else if (failedVideos.length === selectedVideos.length) {
        message.error({ content: '下载失败，请重试', key: messageKey });
      } else {
        message.warning({
          content: `已下载 ${selectedVideos.length - failedVideos.length} 个视频，${failedVideos.length} 个失败`,
          key: messageKey,
        });
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <Spin spinning={categoryLoading}>
        {categoryError ? (
          <Empty description={categoryError} />
        ) : !categoryLoading && !categories.length ? (
          <Empty description="暂无视频分类" />
        ) : (
          <ItemTabs
            items={categories}
            activeType={activeCategory}
            onChange={(category) => setActivePath(category.path)}
          />
        )}
      </Spin>

      {!categoryError && categories.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-4">
            <Input.Search
              allowClear
              value={keyword}
              placeholder="请输入视频名称"
              style={{ width: 320 }}
              onChange={(event) => handleKeywordChange(event.target.value)}
              onSearch={handleKeywordChange}
            />

            <div className="flex items-center gap-3">
              <Checkbox
                checked={allFilteredSelected}
                indeterminate={partiallySelected}
                onChange={(event) => handleSelectAll(event.target.checked)}
              >
                全选
              </Checkbox>
              <span className="text-sm text-slate-500">
                已选择 {selectedPaths.length} 个视频
              </span>
              <Button loading={downloadLoading} onClick={handleDownload}>
                下载
              </Button>
            </div>
          </div>

          <Spin spinning={videoLoading}>
            {videoError ? (
              <Empty description={videoError} />
            ) : pagedVideos.length ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {pagedVideos.map((video) => (
                  <div
                    key={video.path}
                    className="overflow-hidden rounded-lg border border-slate-200 bg-white transition-shadow hover:shadow-md"
                  >
                    <div className="relative flex h-40 items-center justify-center bg-slate-100">
                      <VideoThumbnail video={video} />
                      <button
                        type="button"
                        className="relative z-10 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-slate-900/80 text-white transition-transform hover:scale-105"
                        onClick={() => setActiveVideo(video)}
                        aria-label={`播放 ${video.name}`}
                      >
                        <svg
                          className="ml-1 h-7 w-7"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </button>
                      <Checkbox
                        className="absolute left-3 top-3 z-10"
                        checked={selectedPaths.includes(video.path)}
                        onChange={(event) =>
                          handleSelectVideo(video.path, event.target.checked)
                        }
                      />
                    </div>

                    <div className="space-y-1 p-3">
                      <button
                        type="button"
                        title={video.name}
                        className="block w-full cursor-pointer truncate text-left text-sm font-medium text-slate-800 hover:text-blue-600"
                        onClick={() => setActiveVideo(video)}
                      >
                        {video.name}
                      </button>
                      <div className="flex justify-between text-xs text-slate-500">
                        <span>{formatFileSize(video.size)}</span>
                        <span>
                          {video.modTime
                            ? new Date(video.modTime).toLocaleString()
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Empty
                description={keyword ? '未搜索到相关视频' : '当前赛项暂无视频'}
              />
            )}
          </Spin>

          {filteredVideos.length > 0 && (
            <div className="flex justify-end">
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={filteredVideos.length}
                pageSizeOptions={[12, 24, 48]}
                showSizeChanger
                showTotal={(total) => `共 ${total} 个视频`}
                onChange={(page, size) => {
                  setCurrentPage(size === pageSize ? page : 1);
                  setPageSize(size);
                }}
              />
            </div>
          )}
        </>
      )}

      <Modal
        open={Boolean(activeVideo)}
        title={activeVideo?.name}
        width="min(960px, 90vw)"
        footer={null}
        destroyOnHidden
        onCancel={() => setActiveVideo(null)}
      >
        {activeVideo && (
          <video
            key={activeVideo.url}
            controls
            autoPlay
            preload="metadata"
            className="max-h-[70vh] w-full bg-black"
            src={activeVideo.url}
            onError={() => message.error('视频加载失败')}
          />
        )}
      </Modal>
    </div>
  );
}
